import { ok } from 'neverthrow';
import type { ActionResult } from '@cotera/client/v0/actions/framework';
import { toast } from '@cotera/client/app/components/ui';
import type { WorkflowGraphApiData } from './workflow-graph.types';
import type { WorkflowPasteAnchor } from './workflow-draft-graph';
import { isWorkflowTriggerFlowNodeId } from './workflow-flow-node-ids';
import { listHiddenForEachInteriorNodeIds } from './components/node/edit-strategies/for-each-child-node';
import type { WorkflowModel } from './workflow.model';

export type WorkflowClipboardNode = WorkflowGraphApiData['nodes'][number];

export type WorkflowClipboardEdge = {
  readonly fromNodeId: string;
  readonly toNodeId: string;
};

/**
 * A copied slice of the graph: the nodes themselves plus the edges that ran
 * *between* them, and separately the edges that fed the slice from outside it.
 *
 * Ids are the originals; `draftPasteWorkflowNodes` mints fresh ones on paste, so
 * one copy can be pasted any number of times.
 */
export type WorkflowClipboardPayload = {
  readonly nodes: readonly WorkflowClipboardNode[];
  readonly edges: readonly WorkflowClipboardEdge[];
  /**
   * What fed the slice: edges from a node *outside* it into one inside.
   *
   * Not part of what was copied — this is the anchor a paste re-uses to attach
   * the copies where the originals sat, so a duplicate lands beside its original
   * instead of floating free. Floating is not a neutral state here: a node with
   * no incoming edge is an *entry node*, so the canvas wires it to the trigger
   * and the run engine starts it alongside the real entry point.
   *
   * Ignored when the ids no longer resolve, which is every cross-workflow paste.
   */
  readonly inboundEdges: readonly WorkflowClipboardEdge[];
  /**
   * The subset of `nodes` that never renders: for-each loop bodies, pulled in
   * by `collectHiddenInteriorNodeIds`.
   *
   * They have to travel — a for-each copied without its body arrives doing
   * nothing — but they are not what the user picked, so counting them in a
   * toast reports a number the canvas cannot account for. Ids rather than a
   * count because a payload outlives the graph it came from.
   */
  readonly hiddenNodeIds: readonly string[];
};

/**
 * Nodes hidden inside a for-each loop that belong to one of `rootIds`.
 *
 * The loop body doesn't render on the canvas, so it can never be part of a
 * selection — but copying a for-each without its body would produce a loop that
 * does nothing. Walks outward from the copied nodes through hidden interior
 * nodes only, so an unrelated loop's body elsewhere in the graph stays put.
 */
function collectHiddenInteriorNodeIds(
  graph: WorkflowGraphApiData,
  rootIds: ReadonlySet<string>
): ReadonlySet<string> {
  const hidden = listHiddenForEachInteriorNodeIds(graph.nodes, graph.edges);
  const collected = new Set<string>();
  const queue = [...rootIds];
  while (queue.length > 0) {
    const id = queue.pop();
    if (id === undefined) {
      break;
    }
    for (const edge of graph.edges) {
      if (edge.fromNodeId !== id) {
        continue;
      }
      const next = edge.toNodeId;
      if (!hidden.has(next) || collected.has(next) || rootIds.has(next)) {
        continue;
      }
      collected.add(next);
      queue.push(next);
    }
  }
  return collected;
}

/**
 * Builds the clipboard payload for a canvas selection.
 *
 * Trigger flow nodes are skipped: a trigger is a binding to a row that lives
 * outside the workflow, so "copying" one would either duplicate a schedule or
 * silently bind the same trigger twice. Returns null when nothing copyable is
 * selected, so callers can leave Cmd-C to the browser.
 */
export function collectWorkflowClipboardPayload(
  graph: WorkflowGraphApiData,
  selectedFlowNodeIds: readonly string[]
): WorkflowClipboardPayload | null {
  const byId = new Map(graph.nodes.map((node) => [node.id, node] as const));
  const selected = new Set(
    selectedFlowNodeIds.filter(
      (nodeId) => !isWorkflowTriggerFlowNodeId(nodeId) && byId.has(nodeId)
    )
  );
  if (selected.size === 0) {
    return null;
  }

  const interior = collectHiddenInteriorNodeIds(graph, selected);
  const included = new Set([...selected, ...interior]);

  const asClipboardEdge = (
    edge: WorkflowGraphApiData['edges'][number]
  ): WorkflowClipboardEdge => ({
    fromNodeId: edge.fromNodeId,
    toNodeId: edge.toNodeId,
  });

  return {
    // Graph order rather than click order, so the same selection always copies
    // the same payload.
    nodes: graph.nodes.filter((node) => included.has(node.id)),
    edges: graph.edges
      .filter(
        (edge) => included.has(edge.fromNodeId) && included.has(edge.toNodeId)
      )
      .map(asClipboardEdge),
    inboundEdges: graph.edges
      .filter(
        (edge) => !included.has(edge.fromNodeId) && included.has(edge.toNodeId)
      )
      .map(asClipboardEdge),
    hiddenNodeIds: [...interior],
  };
}

/**
 * The copied slice, held in memory for the tab.
 *
 * Module scope rather than component state so a copy survives the canvas
 * unmounting — navigating to another workflow and pasting there is the whole
 * point. Deliberately not the system clipboard: the payload is graph rows, not
 * text, and reading the system clipboard needs a permission prompt that a
 * keystroke shouldn't trigger.
 */
let clipboard: WorkflowClipboardPayload | null = null;

export function setWorkflowClipboard(payload: WorkflowClipboardPayload): void {
  clipboard = payload;
}

export function readWorkflowClipboard(): WorkflowClipboardPayload | null {
  return clipboard;
}

/** Test seam — resets the module-level clipboard between cases. */
export function clearWorkflowClipboard(): void {
  clipboard = null;
}

/** The copyable part of the canvas selection, or null when there is none. */
export function workflowSelectionPayload(
  model: WorkflowModel
): WorkflowClipboardPayload | null {
  const graph = model.graphWatchable.snapshot();
  if (graph === null) {
    return null;
  }
  return collectWorkflowClipboardPayload(
    graph,
    model.editorSelectionWatchable.snapshot().selectedNodeIds
  );
}

/**
 * Where a paste lands: after the selected node when exactly one is selected,
 * and otherwise beside the originals.
 *
 * One selected node reads as "put it here"; anything else has no obvious
 * insertion point. Triggers never count — they are not part of the flow the
 * copies join.
 */
export function workflowPasteAnchor(model: WorkflowModel): WorkflowPasteAnchor {
  const selected = model.editorSelectionWatchable
    .snapshot()
    .selectedNodeIds.filter((nodeId) => !isWorkflowTriggerFlowNodeId(nodeId));
  const anchorNodeId = selected.length === 1 ? selected[0] : undefined;
  return anchorNodeId === undefined
    ? { kind: 'source-inputs' }
    : { kind: 'anchor-node', nodeId: anchorNodeId };
}

const nodeNoun = (count: number): string =>
  count === 1 ? '1 node' : `${count} nodes`;

/**
 * How many of the copied nodes are on the canvas.
 *
 * What every count the user sees is stated in: they selected the nodes they can
 * see, and a paste puts back exactly one node per payload node, so the hidden
 * loop bodies are the whole difference on both ends.
 */
export function visibleNodeCount(payload: WorkflowClipboardPayload): number {
  return payload.nodes.length - payload.hiddenNodeIds.length;
}

/** `Copied 2 nodes`, for the action that just filled the clipboard. */
export function reportWorkflowCopy(payload: WorkflowClipboardPayload): void {
  toast.success(`Copied ${nodeNoun(visibleNodeCount(payload))}`);
}

/**
 * Pastes a slice into the draft and reports what landed.
 *
 * `verb` names the gesture — the same paste is a "Paste" or a "Duplicate"
 * depending on where the payload came from.
 */
export function pasteWorkflowSlice(params: {
  model: WorkflowModel;
  payload: WorkflowClipboardPayload;
  anchor: WorkflowPasteAnchor;
  verb: string;
}): ActionResult<{ t: string }[]> {
  const { model, payload, anchor, verb } = params;
  // Read before the paste: afterwards the slice's own edges are in the graph,
  // which would make a connected slice pasted into an empty workflow look like
  // it had landed loose in a wired one.
  const wasWired = (model.graphWatchable.snapshot()?.edges.length ?? 0) > 0;

  const { nodeIds, attached } = model.pasteNodes(payload, anchor);
  if (nodeIds.length === 0) {
    return ok({});
  }

  // Landing loose is fine in an empty graph and surprising in a wired one:
  // an unconnected node is an entry node, so it starts running on its own.
  const loose = attached === false && wasWired;
  const landed = nodeNoun(visibleNodeCount(payload));
  toast.success(
    loose ? `${verb} ${landed} — not connected` : `${verb} ${landed}`
  );
  return ok({});
}
